// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The XR session manager (M5-01).
//
// Starts and ends `immersive-vr` and `immersive-ar` sessions, tells the renderer about them, and
// says what happened. It is the only place in the product that touches `navigator.xr`.
//
// **Ports, not globals.** Everything the manager needs arrives as a narrow structural type:
// `navigator.xr`, and the two methods a Three.js renderer exposes for XR. That is not test
// decoration — it is the only way any of this is testable at all, because `environment: 'node'`
// has no `navigator`, no WebGL and certainly no headset, and a session manager nobody can test
// is a session manager whose error paths are discovered on a Quest.
//
// **The manager does not own the frame loop.** Entering XR moves the loop from the browser's
// `requestAnimationFrame` to the session's, which is a change the *runtime* has to make because
// the runtime is what has a loop. The manager reports the boundary and stays out of it: a
// session manager that also drove frames would be two owners of the clock, and the one invariant
// this renderer has is that the clock has one owner (`SceneHost`).

import { featuresFor, grantedFeatures, type ImmersiveMode } from './features'

/** The slice of `navigator.xr` this product uses. */
export interface XRSystemPort {
  isSessionSupported: (mode: string) => Promise<boolean>
  requestSession: (mode: string, init?: unknown) => Promise<XRSessionPort>
}

/** The slice of an `XRSession` this product uses. */
export interface XRSessionPort {
  readonly enabledFeatures?: readonly string[]
  /**
   * How the runtime composites the page over the world: `opaque` (VR), `alpha-blend` (passthrough),
   * `additive`. Read for the session's opening line in development, because "hands are not
   * visible" in an `immersive-ar` session is a question about this value before anything else.
   */
  readonly environmentBlendMode?: string
  /** Ask the runtime for a space of this kind, *now*. See `floorAlignment.ts`. */
  requestReferenceSpace?: (type: string) => Promise<unknown>
  end: () => Promise<void>
  addEventListener: (type: string, listener: () => void) => void
  removeEventListener: (type: string, listener: () => void) => void
}

/** The slice of a Three.js renderer this product hands a session to. */
export interface XRRendererPort {
  xr: {
    enabled: boolean
    setSession: (session: XRSessionPort | null) => Promise<void> | void
    setReferenceSpaceType?: (type: string) => void
    /**
     * Install a reference space directly (M5-33).
     *
     * `setReferenceSpaceType` only says which *kind* to ask for, and only before the session
     * starts. Re-reading the runtime's floor mid-session, and offsetting it when it turns out to
     * be impossible, both need to hand over an actual space — and offsetting one is the only
     * lever in WebXR that can move the world's height at all. Optional because a renderer that
     * cannot do it is a renderer whose floor is as good as it is going to get.
     */
    setReferenceSpace?: (space: unknown) => void
    getReferenceSpace?: () => unknown
  }
}

/** Why a session is not running. */
export type XRUnavailable =
  | 'no-webxr'
  | 'mode-unsupported'
  | 'refused'
  | 'insecure-context'

/** What the manager is doing right now. */
export type XRPhase = 'idle' | 'starting' | 'presenting' | 'ending'

export interface XRSessionEvents {
  /** A session started. `granted` is the optional features the runtime actually gave. */
  onStart?: (mode: ImmersiveMode, granted: ReadonlySet<string>) => void
  /**
   * A session ended, and whether the product asked for it.
   *
   * `deliberate` is false when the runtime ended it — the player took the headset off and the
   * session timed out, the system menu took over, or the browser lost its activity (06 §7). The
   * distinction matters: a deliberate exit returns the player to the windowed view, and an
   * involuntary one keeps the game paused and offers to go back in.
   */
  onEnd?: (mode: ImmersiveMode, deliberate: boolean) => void
  onUnavailable?: (mode: ImmersiveMode, reason: XRUnavailable) => void
}

export interface XRSessionManagerOptions {
  readonly xr: XRSystemPort | undefined
  readonly renderer: XRRendererPort
  /** Reference space to present in. `local-floor` everywhere; see `features.ts`. */
  readonly referenceSpace?: string
  readonly events?: XRSessionEvents
}

export class XRSessionManager {
  private readonly xr: XRSystemPort | undefined
  private readonly renderer: XRRendererPort
  private readonly referenceSpace: string
  private readonly events: XRSessionEvents

  private session: XRSessionPort | undefined
  private currentMode: ImmersiveMode | undefined
  private phase: XRPhase = 'idle'
  /** True between `end()` being called and the `end` event arriving. */
  private endingDeliberately = false
  private granted: ReadonlySet<string> = new Set()

  constructor(options: XRSessionManagerOptions) {
    this.xr = options.xr
    this.renderer = options.renderer
    this.referenceSpace = options.referenceSpace ?? 'local-floor'
    this.events = options.events ?? {}
  }

  get state(): XRPhase {
    return this.phase
  }

  /** The mode being presented, or undefined. */
  get mode(): ImmersiveMode | undefined {
    return this.phase === 'presenting' ? this.currentMode : undefined
  }

  /** True only while actually presenting — the same question `ProbeHost.xr` asks. */
  get presenting(): boolean {
    return this.phase === 'presenting'
  }

  /**
   * The running session, or undefined.
   *
   * Exposed for exactly one caller: re-reading the runtime's floor needs to ask the *session*,
   * and the session is the one thing in this manager that nothing else could reconstruct.
   */
  get current(): XRSessionPort | undefined {
    return this.phase === 'presenting' ? this.session : undefined
  }

  /** The optional features the runtime granted for the running session. */
  get features(): ReadonlySet<string> {
    return this.granted
  }

  /**
   * Can this device present this mode?
   *
   * Answers false rather than throwing on a browser with no `navigator.xr`, which is most of
   * them: WebXR support is a fact about the visitor, not an error in the product, and the
   * windowed view is a complete game.
   */
  async supports(mode: ImmersiveMode): Promise<boolean> {
    if (this.xr === undefined) return false
    try {
      return await this.xr.isSessionSupported(mode)
    } catch {
      // Some runtimes reject rather than resolve false — notably for `immersive-ar` on a headset
      // with no passthrough permission. Same answer either way.
      return false
    }
  }

  /**
   * Enter a mode. Resolves true if a session is now presenting.
   *
   * Every failure calls `onUnavailable` with a reason rather than throwing, because every caller
   * is a button and every button needs a sentence to show. A rejected `requestSession` is
   * `refused` — the commonest cause is the call not being inside a user gesture, which is a
   * programming error the console will also complain about, and the second commonest is the
   * player declining the runtime's own permission prompt, which is not.
   */
  async enter(mode: ImmersiveMode): Promise<boolean> {
    // **Every way out of here is said on the console** (13 Sep 2026, development only). "Web page
    // not working to start the MR" arrived with a page that had logged nothing since it loaded:
    // a press that finds the manager `starting` (a permission prompt still up) or `ending` (a
    // session the browser never finished closing) returns without a sentence, and a request the
    // runtime refuses is a sentence on the page and nothing in the tap.
    if (this.phase !== 'idle') {
      say(`[xr] enter ${mode}: not now — the manager is ${this.phase}${this.currentMode === undefined ? '' : ` (${this.currentMode})`}`)
      return this.phase === 'presenting' && this.currentMode === mode
    }
    if (this.xr === undefined) {
      say(`[xr] enter ${mode}: no WebXR on this browser`)
      this.events.onUnavailable?.(mode, 'no-webxr')
      return false
    }

    this.phase = 'starting'
    say(`[xr] enter ${mode}: asking the runtime`)
    let supported: boolean
    try {
      supported = await this.xr.isSessionSupported(mode)
    } catch {
      supported = false
    }
    if (!supported) {
      this.phase = 'idle'
      say(`[xr] enter ${mode}: the runtime does not support it`)
      this.events.onUnavailable?.(mode, 'mode-unsupported')
      return false
    }

    let session: XRSessionPort
    try {
      const { requiredFeatures, optionalFeatures } = featuresFor(mode)
      session = await this.xr.requestSession(mode, {
        requiredFeatures: [...requiredFeatures],
        optionalFeatures: [...optionalFeatures],
      })
    } catch (error) {
      this.phase = 'idle'
      say(`[xr] enter ${mode}: refused — ${error instanceof Error ? `${error.name}: ${error.message}` : String(error)}`)
      this.events.onUnavailable?.(mode, 'refused')
      return false
    }

    this.session = session
    this.currentMode = mode
    this.granted = grantedFeatures(session)
    session.addEventListener('end', this.onSessionEnd)

    this.renderer.xr.enabled = true
    this.renderer.xr.setReferenceSpaceType?.(this.referenceSpace)
    await this.renderer.xr.setSession(session)

    this.phase = 'presenting'
    this.events.onStart?.(mode, this.granted)
    return true
  }

  /** Leave the session the product started. Safe to call when there is none. */
  async exit(): Promise<void> {
    if (this.session === undefined || this.phase === 'ending') return
    this.endingDeliberately = true
    this.phase = 'ending'
    try {
      await this.session.end()
    } catch {
      // A session that refuses to end has usually already ended — the `end` event may or may not
      // still arrive, so tear down here rather than waiting for something that may never come.
      this.onSessionEnd()
    }
  }

  /**
   * Detach without ending anything.
   *
   * For teardown of the page, where ending the session would be both pointless and slower than
   * the unload. Leaves the runtime to collect the session.
   */
  dispose(): void {
    this.session?.removeEventListener('end', this.onSessionEnd)
    this.session = undefined
    this.currentMode = undefined
    this.phase = 'idle'
    this.granted = new Set()
  }

  private readonly onSessionEnd = (): void => {
    const mode = this.currentMode
    const deliberate = this.endingDeliberately
    this.session?.removeEventListener('end', this.onSessionEnd)
    this.session = undefined
    this.currentMode = undefined
    this.endingDeliberately = false
    this.phase = 'idle'
    this.granted = new Set()
    this.renderer.xr.enabled = false
    if (mode !== undefined) this.events.onEnd?.(mode, deliberate)
  }
}

/** Development-only console line, the way `XRRuntime.say` is: inert in production. */
function say(line: string): void {
  if (import.meta.env.DEV) console.info(line)
}

/**
 * `navigator.xr`, or undefined on a browser that has none.
 *
 * Also undefined in an insecure context, because that is where the property is absent — WebXR is
 * secure-context only, so an `http://` page on anything but localhost simply has no `xr` at all.
 * The distinction is worth surfacing to a developer and not to a player, which is why it is a
 * comment here and not a third return value.
 */
export function browserXRSystem(): XRSystemPort | undefined {
  const nav = (globalThis as { navigator?: { xr?: unknown } }).navigator
  const xr = nav?.xr
  if (xr === undefined || xr === null) return undefined
  return xr as XRSystemPort
}

/**
 * Is this page on a headset — can this browser present *either* immersive mode?
 *
 * Asked by a control that has to decide what "open in headset" means before it is pressed: *this*
 * headset, or a code for another one (`PublishPanel`, M13). Both modes are asked because a Quest
 * whose passthrough was declined still presents VR, and either is the same answer here. `false`
 * without a `navigator.xr` and `false` on a runtime that rejects rather than resolves — a fact
 * about the visitor, not an error in the product, exactly as `supports` treats it.
 */
export async function presentsHere(xr: XRSystemPort | undefined = browserXRSystem()): Promise<boolean> {
  if (xr === undefined) return false
  for (const mode of ['immersive-ar', 'immersive-vr'] as const) {
    try {
      if (await xr.isSessionSupported(mode)) return true
    } catch {
      // As `supports`: some runtimes reject for a mode they cannot offer. Ask about the other.
    }
  }
  return false
}
